import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import { useNavigate } from "react-router-dom";
import { useEmployment } from "app/hooks/employment/useEmployment";
import { EMPLOYMENT } from "app/constants/apiEndPoints";
import { useDispatch } from "react-redux";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/employmentDetails";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import { employmentDetailsFromTwo } from "app/utils/types";
import moment from "moment";
import DynamicFormTwo from "./dynamicForms/DynamicFormTwo";
import { handleApiError } from "app/utils/handleApiError";

const PartTwo: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { mutate: submitEmployment, isPending } = useEmployment();
  const { formTwo, ...others } = useSelector(
    (state: RootState) => state.employmentDetails.stepsData
  );
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
    reset,
  } = useForm<employmentDetailsFromTwo>({
    defaultValues: {
      hassecondjob: "no",
      previousemployer: "no",
      employmentType: "yes",
    },
  });
  const joiningDate = watch("joiningDate");
  const hasSecondJob = watch("previousemployer");

  const showEmploymentType =
    !!joiningDate &&
    moment(joiningDate).isBefore(moment()) &&
    moment().diff(moment(joiningDate), "months") <= 12;

  const onSubmit = (data: employmentDetailsFromTwo) => {
    const { previousemployer, joiningDate, hassecondjob } = data;
    const formattedData: Partial<any> = {
      ...data,
      joiningDate,
      employerName: others?.formOne?.employerName ?? "",
    };

    if (previousemployer === "no") delete formattedData.previousemployer;
    if (hassecondjob === "no") delete formattedData.hassecondjob;
    if (!showEmploymentType) delete formattedData.employmentType;

    dispatch(
      addStepsData({
        ...others,
        formTwo: data,
      })
    );

    if (previousemployer === "no" && hassecondjob === "no") {
      submitEmployment(
        {
          url: `${EMPLOYMENT}/${savedIds?.employmentID}/second`,
          data: formattedData,
        },
        {
          onSuccess: () => {
            navigate(`/income-details`);
          },
          onError: (error: any) => {
            handleApiError(error);
          },
        }
      );
    } else {
      dispatch(nextStep());
    }
  };

  useEffect(() => {
    if (formTwo && formTwo?.joiningDate) {
      reset(formTwo);
    }
  }, [formTwo, reset]);

  useEffect(() => {
    if (hasSecondJob === "yes") {
      setValue("hassecondjob", "no");
      clearErrors("hassecondjob");
    }
  }, [hasSecondJob, setValue, clearErrors]);

  useEffect(() => {
    if (!formTwo?.employmentType) return;

    const typeMapping: Record<string, string> = {
      "full-time": "yes",
      "part-time": "no",
    };

    setValue(
      "employmentType",
      typeMapping[formTwo.employmentType] ?? formTwo.employmentType
    );
  }, [formTwo?.employmentType, setValue]);

  return (
    <>
      <ProgressBar progress={25} MB={7} />
      <div className="w-full  sm:w-[70%] xl:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Employment Details" />

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
          <DynamicFormTwo
            control={control}
            errors={errors}
            register={register}
            setValue={setValue}
            watch={watch}
            joiningDate={"joiningDate"}
            employmentType={"employmentType"}
            previousemployer={"previousemployer"}
            showEmploymentType={showEmploymentType}
            hasSecondJob={hasSecondJob}
            hasSecondjobName={"hassecondjob"}
          />

          <div className="flex justify-between my-10">
            <PreviousButtom
              onClick={() => {
                dispatch(prevStep());
              }}
            >
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartTwo;
